import { useEffect, useState } from 'react';
import { joinCatalogue } from '../../domain/cars/carCatalogue';
import { fetchCatalogue, fetchProse } from '../../infrastructure/content/carContentClient';
import { useLocale } from '../i18n/LocaleProvider';

/**
 * The catalogue, joined to the prose of the active language.
 *
 * Cached at module scope and keyed by language, because the garage and the car
 * detail page are separate routes: without this, opening a car and pressing back
 * would refetch the whole catalogue and flash a loading state over content the
 * reader was just looking at. A PROMISE rather than a result, so two screens
 * mounting in the same tick share one request rather than racing two.
 */
const cache = new Map();

function loadCatalogue(languageCode) {
    if (!cache.has(languageCode)) {
        const pending = Promise.all([fetchCatalogue(), fetchProse(languageCode)])
            .then(([cars, { prose, fallbackProse }]) => joinCatalogue(cars, prose, fallbackProse))
            // A failed load must not poison the cache — the next mount should be
            // allowed to try again rather than replaying the rejection forever.
            .catch((error) => {
                cache.delete(languageCode);

                throw error;
            });

        cache.set(languageCode, pending);
    }

    return cache.get(languageCode);
}

export function useCarCatalogue() {
    const { languageCode } = useLocale();
    const [state, setState] = useState({ status: 'loading', cars: [], error: null });

    useEffect(() => {
        let active = true;

        setState({ status: 'loading', cars: [], error: null });

        loadCatalogue(languageCode).then(
            (cars) => active && setState({ status: 'ready', cars, error: null }),
            (error) => active && setState({ status: 'failed', cars: [], error }),
        );

        // The language can change under a mounted screen via the switcher, so a
        // resolved fetch for the previous language must not land.
        return () => {
            active = false;
        };
    }, [languageCode]);

    return state;
}

/** Test seam — the module cache would otherwise leak one spec's catalogue into the next. */
export function clearCarCatalogueCache() {
    cache.clear();
}
